import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listPositions } from '@/lib/positionsApi'
import { useWhatIfCommitMutation, useWhatIfPreview, useWhatIfQuickFill } from '@/lib/useSimulation'
import { WhatIfForm, type SimulationPosition } from './WhatIfForm'
import { WhatIfOutput } from './WhatIfOutput'
import type { ReinvestTargetInput, SimulationResult } from '@/types/simulation'

interface Props {
  // Uncontrolled (modal-variant): pass initial values
  initialPositionId?: number | null
  initialSellPct?: string
  // Controlled (page-variant): parent owns state
  positionId?: number | null
  sellPct?: string
  onPositionIdChange?: (id: number | null) => void
  onSellPctChange?: (pct: string) => void
  // Callback when commit succeeds
  onCommitSuccess?: (orderTicketIds: number[]) => void
}

function extractPreviewError(err: unknown): string {
  if (!err) return 'Unbekannter Fehler.'
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String(d.msg) : null))
      .filter((m): m is string => m !== null)
    if (messages.length > 0) return messages.join(' · ')
  }
  return e.message ?? 'Unbekannter Fehler.'
}

export function WhatIfBody({
  initialPositionId = null,
  initialSellPct = '0',
  positionId: controlledPositionId,
  sellPct: controlledSellPct,
  onPositionIdChange,
  onSellPctChange,
  onCommitSuccess,
}: Props) {
  // Internal state — only used when NOT controlled
  const [internalPositionId, setInternalPositionId] = useState<number | null>(initialPositionId)
  const [internalSellPct, setInternalSellPct] = useState(initialSellPct)
  const [reinvestTargets, setReinvestTargets] = useState<ReinvestTargetInput[]>([])
  const [committedTicketCount, setCommittedTicketCount] = useState<number | null>(null)
  const [newAssetOptions, setNewAssetOptions] = useState<{ isin: string; label: string }[]>([])
  const [skippedNoPrice, setSkippedNoPrice] = useState<string[]>([])
  // Cache last successful preview so cards stay visible while a new debounced
  // call is in flight (useMutation resets .data to undefined on every mutate()).
  const [lastPreview, setLastPreview] = useState<SimulationResult | null>(null)

  // Resolve controlled vs. uncontrolled
  const isControlled = onPositionIdChange !== undefined
  const positionId = isControlled ? (controlledPositionId ?? null) : internalPositionId
  const sellPct = isControlled ? (controlledSellPct ?? '0') : internalSellPct

  const handlePositionChange = (id: number | null) => {
    if (isControlled) {
      onPositionIdChange(id)
    } else {
      setInternalPositionId(id)
    }
    setCommittedTicketCount(null)
    setLastPreview(null)
    setNewAssetOptions([])
    setReinvestTargets([])
  }

  const handleSellPctChange = (pct: string) => {
    if (isControlled) {
      onSellPctChange?.(pct)
    } else {
      setInternalSellPct(pct)
    }
    setCommittedTicketCount(null)
  }

  const positionsQuery = useQuery({
    queryKey: ['positions'],
    queryFn: listPositions,
  })

  const preview = useWhatIfPreview()
  const quickFill = useWhatIfQuickFill()
  const commit = useWhatIfCommitMutation()

  // Effect-level debounce: stable primitive deps prevent the timer from being
  // reset on every render (the previous useDebounce-on-object version did
  // exactly that, breaking live updates).
  useEffect(() => {
    if (!positionId || Number(sellPct) <= 0) return
    const t = setTimeout(() => {
      preview.mutate(
        {
          sell_position_id: positionId,
          sell_pct_of_position: sellPct,
          reinvest_targets: reinvestTargets,
        },
        { onSuccess: (data) => setLastPreview(data) },
      )
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, sellPct, reinvestTargets])

  const simPositions: SimulationPosition[] = (positionsQuery.data ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    ticker: p.ticker ?? null,
    total_quantity: p.total_quantity,
    position_value_eur: p.position_value_eur ?? '0',
  }))

  const onQuickFill = async () => {
    if (!positionId) return
    setSkippedNoPrice([])
    const selected = simPositions.find((p) => p.id === positionId)
    if (!selected) return
    const amount = String(Number(selected.position_value_eur) * Number(sellPct))
    const suggestions = await quickFill.mutateAsync({
      sell_position_id: positionId,
      amount_eur: amount,
    })
    setNewAssetOptions(
      suggestions
        .filter((s) => s.is_new_position && s.isin !== null)
        .map((s) => ({ isin: s.isin as string, label: s.label })),
    )
    setReinvestTargets(
      suggestions.map((s) => ({
        position_id: s.position_id,
        isin: s.isin && s.is_new_position ? s.isin : null,
        is_cash: s.is_cash,
        pct: s.pct,
      })),
    )
  }

  const onCommit = async () => {
    if (!positionId) return
    setSkippedNoPrice([])
    const response = await commit.mutateAsync({
      sell_position_id: positionId,
      sell_pct_of_position: sellPct,
      reinvest_targets: reinvestTargets,
    })
    setCommittedTicketCount(response.order_ticket_ids.length)
    setSkippedNoPrice(response.skipped_no_price ?? [])
    onCommitSuccess?.(response.order_ticket_ids)
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[320px_1fr]">
      <WhatIfForm
        positions={simPositions}
        selectedPositionId={positionId}
        onPositionChange={handlePositionChange}
        sellPct={sellPct}
        onSellPctChange={handleSellPctChange}
        reinvestTargets={reinvestTargets}
        onReinvestTargetsChange={setReinvestTargets}
        onQuickFill={onQuickFill}
        isQuickFillLoading={quickFill.isPending}
        newAssetOptions={newAssetOptions}
      />
      <div className="space-y-3">
        {committedTicketCount !== null && (
          <div
            role="status"
            className="border border-gain/40 bg-gain/10 px-4 py-3 text-sm text-gain"
          >
            <strong>
              {committedTicketCount === 1
                ? '1 Order-Ticket erzeugt.'
                : `${committedTicketCount} Order-Tickets erzeugt.`}
            </strong>{' '}
            <span className="text-ink">
              Trage den Inhalt in IBKR ein und bestätige dort manuell.
            </span>
          </div>
        )}
        {skippedNoPrice.length > 0 && (
          <div
            role="status"
            className="border border-bernstein/40 bg-bernstein/10 px-4 py-3 text-sm text-bernstein-dim"
          >
            Kurs nicht verfügbar — manuell kaufen: {skippedNoPrice.join(', ')}
          </div>
        )}
        {preview.isError && (
          <div
            role="alert"
            className="border border-loss/40 bg-loss/10 px-4 py-3 text-sm text-loss"
          >
            <strong>Preview fehlgeschlagen.</strong>{' '}
            <span className="text-ink">{extractPreviewError(preview.error)}</span>
          </div>
        )}
        {lastPreview && <WhatIfOutput result={lastPreview} />}
        {!lastPreview && committedTicketCount === null && (
          <div className="border border-hairline bg-panel p-6 text-sm text-muted">
            Wähle eine Position und gib eine Verkaufs-Menge ein.
          </div>
        )}
        <div className="flex justify-end">
          <button
            disabled={!lastPreview || commit.isPending}
            onClick={onCommit}
            className="border border-bernstein bg-bernstein/10 px-4 py-2 text-[11px] tracked-caps text-bernstein transition-colors hover:bg-bernstein/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {commit.isPending ? 'Wird erzeugt…' : 'Order-Tickets erzeugen →'}
          </button>
        </div>
      </div>
    </div>
  )
}
